import React, { useState, useEffect, useCallback } from 'react';
import { X, Printer, Upload, FileCheck, RotateCcw } from 'lucide-react';

// Levantine Arabic month names, as used on the printed payment pledge
const ARABIC_MONTHS = ['كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران',
                       'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول'];
const fmtArabicDate = (d) => {
  const dt = new Date(d);
  return `${dt.getDate()} ${ARABIC_MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
};

const escapeHtml = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
// Same, but keep the user's line breaks: a raw \n inside a <p> collapses to a
// space when printed, so turn each newline into a <br> after escaping.
const escapeHtmlMultiline = (s) => escapeHtml(s).replace(/\r\n|\r|\n/g, '<br>');

// Browser-side scan compression. A signed A4 page stays perfectly legible at
// ~1600px / 70% JPEG while dropping from megabytes to a few hundred KB, so the
// bytes we store in the DB (and back up) stay small. PDFs and images the
// browser can't decode (e.g. iPhone HEIC) pass through untouched.
const SCAN_MAX_DIM = 1600;
const SCAN_JPEG_QUALITY = 0.7;

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const compressScan = async (file) => {
  const original = await fileToDataUrl(file);
  if (!file.type.startsWith('image/')) {
    return { fileName: file.name, fileData: original };
  }
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, SCAN_MAX_DIM / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff'; // flatten transparency to white
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', SCAN_JPEG_QUALITY));
      };
      img.onerror = reject;
      img.src = original;
    });
    // Keep whichever is smaller — already-tiny images shouldn't grow
    if (dataUrl.length < original.length) {
      const base = file.name.replace(/\.[^.]+$/, '');
      return { fileName: `${base}.jpg`, fileData: dataUrl };
    }
  } catch (_) { /* undecodable — fall through to original */ }
  return { fileName: file.name, fileData: original };
};

const dataUrlKB = (dataUrl) => Math.round((dataUrl.length * 3 / 4) / 1024);

// Printable payment pledge (تعهد) parents sign — mirrors the school's Word
// template. Printing SAVES the wording + a snapshot of the plan rows to the
// server so the exact signed document can be reprinted later, and a scanned
// copy of the signed paper can be attached. Editing the plan and printing
// again replaces the saved pledge and clears the old scan.
const RegistrationPledgePrintModal = ({
  apiCall, registration, regChildren, paymentPlan, pledgeDiscounts, registrationYear,
  allowEdit = true, // false = reprint-only (no editing / no Print & Save)
  showSuccess, showError, onClose
}) => {
  const registrationId = registration.RegistrationID;
  const [saved, setSaved] = useState(null);      // existing saved pledge (or null)
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Rows to print: from the saved snapshot when reprinting, else the live plan
  const buildRowsFromPlan = () => paymentPlan.map(i => ({
    date: i.DueDate, lbp: i.AmountLBP || 0, usd: i.AmountUSD || 0
  }));

  // Standby discounts (informational): named lines printed on the pledge that do
  // NOT change the installments. From the live registration when printing new;
  // from the saved snapshot when reprinting.
  const buildDiscountsFromProp = () =>
    (Array.isArray(pledgeDiscounts) ? pledgeDiscounts : []).map(d => ({
      name: d.name || '', amount: Number(d.amount) || 0,
      currency: d.currency === 'LBP' ? 'LBP' : 'USD'
    }));

  const [form, setForm] = useState(() => {
    // The pledged total is what the family is responsible for = the scheduled
    // installments PLUS the standby discounts (which were netted out of the
    // schedule). So installments + discounts add back up to this figure.
    const disc = Array.isArray(pledgeDiscounts) ? pledgeDiscounts : [];
    const discLBP = disc.filter(d => d.currency === 'LBP').reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const discUSD = disc.filter(d => d.currency !== 'LBP').reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const totLBP = paymentPlan.reduce((s, i) => s + (i.AmountLBP || 0), 0) + discLBP;
    const totUSD = paymentPlan.reduce((s, i) => s + (i.AmountUSD || 0), 0) + discUSD;
    const [y1, y2] = registrationYear.split('-');
    const amounts = [
      totLBP > 0 ? `${totLBP.toLocaleString()} ل.ل` : null,
      totUSD > 0 ? `${totUSD.toLocaleString()}$` : null
    ].filter(Boolean).join(' و');
    const childNames = regChildren.map(c => c.FirstName || c.FullName).join(' و ');
    return {
      signer: `انا الموقع أدناه السيد ${registration.FatherName} وبصفتي والد/ة التلميذ/ة ${childNames} ،`,
      pledge: `أتعهد أن أسدد ما هو متوجب بذمتي من أقساط مدرسية المستحقة عن العام الدراسي 20${y1}-20${y2} ، والبالغ قيمته ${amounts} وذلك بحسب الجدول التالي :`,
      closing: 'تمت هذه الجدولة بطلب من الاهل .',
      signDate: fmtArabicDate(new Date())
    };
  });

  const loadSaved = useCallback(async () => {
    try {
      const p = await apiCall(`/registration/${registrationId}/pledge`);
      setSaved(p);
      if (p) {
        setForm({
          signer: p.SignerText || '',
          pledge: p.PledgeText || '',
          closing: p.ClosingText || '',
          signDate: p.SignDateText || fmtArabicDate(new Date())
        });
      }
    } catch (_) { /* none saved yet */ }
    finally { setLoading(false); }
  }, [apiCall, registrationId]);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  // Arabic amount with the currency symbol used across the pledge.
  const fmtPledgeAmount = (amount, currency) =>
    currency === 'LBP' ? `${Number(amount).toLocaleString()} ل.ل` : `${Number(amount).toLocaleString()}$`;

  // rows / discounts params let Reprint use the saved snapshot; default = live plan
  const buildPledgeHTML = (rows, discounts = []) => {
    const TD = 'border:1px solid #444;padding:9px 12px;text-align:right;font-size:17px;';
    const body = rows.map(r => `
      <tr>
        <td style="${TD}">${fmtArabicDate(r.date)}</td>
        <td style="${TD}">${r.lbp > 0 ? `${Number(r.lbp).toLocaleString()} ل.ل` : ''}</td>
        <td style="${TD}">${r.usd > 0 ? `${Number(r.usd).toLocaleString()}$` : ''}</td>
      </tr>`).join('');
    // Standby discounts block — printed only when there are any.
    const discountRows = (discounts || []).filter(d => d.name && d.amount > 0);
    const discountsHTML = discountRows.length ? `
      <div style="margin:0 0 16px 0;">
        <div style="font-weight:bold;margin-bottom:2px;">: الحسومات</div>
        <div style="font-weight:normal;font-size:14px;margin-bottom:6px;">(لا تنفذ الحسومات إلا عند تسديد كامل الأقساط )</div>
        <table style="width:70%;border-collapse:collapse;">
          ${discountRows.map(d => `
            <tr>
              <td style="${TD}">${escapeHtml(d.name)}</td>
              <td style="${TD}white-space:nowrap;">${fmtPledgeAmount(d.amount, d.currency)}</td>
            </tr>`).join('')}
        </table>
      </div>` : '';
    return `
      <div style="text-align:right;font-size:18px;">جانب إدارة معهد الرسل – جونية المحترمين</div>
      <div style="text-align:center;font-weight:bold;font-size:24px;margin:20px 0;text-decoration:underline;">- تعهد -</div>
      <p style="margin:0 0 10px 0;">${escapeHtmlMultiline(form.signer)}</p>
      <p style="margin:0 0 18px 0;">${escapeHtmlMultiline(form.pledge)}</p>
      <table style="width:100%;border-collapse:collapse;margin-bottom:18px;">
        <thead>
          <tr style="background:#f3f4f6;font-weight:bold;">
            <td style="${TD}width:34%;">التاريخ</td>
            <td style="${TD}width:33%;">القسط المدرسي بالليرة اللبنانية</td>
            <td style="${TD}width:33%;">القسط المدرسي بالدولار الأميركي</td>
          </tr>
        </thead>
        <tbody>${body}</tbody>
      </table>
      ${discountsHTML}
      <p style="margin:0 0 10px 0;">كما اتعهد بتسديد الزيادات في حال أُقِرَّت</p>
      <p style="margin:0 0 34px 0;">${escapeHtmlMultiline(form.closing)}</p>
      <div style="display:flex;justify-content:space-between;margin-top:28px;">
        <span>التاريخ: ${escapeHtml(form.signDate)}</span>
        <span>إمضاء الأهل: ................</span>
        <span>الإدارة: ................</span>
      </div>`;
  };

  // Print so the pledge ALWAYS fits on one A4 page, however long the plan is.
  //
  // The page is a fixed 210×297mm "sheet" with `overflow:hidden` printed with a
  // zero @page margin, so the sheet IS exactly one physical page — it physically
  // cannot spill onto a second one. The margins live as padding inside the sheet.
  // The content sits in an inner box; if it's taller than the sheet's inner area
  // we scale it down with `transform` (measured via a px-per-mm probe so the math
  // holds at any print resolution). `transform` is honoured in print where `zoom`
  // is not, and the hidden overflow is a hard backstop against a second page.
  const PAGE = { padTop: 10, padBottom: 14, padSide: 18 }; // mm
  const sendToPrinter = (html) => {
    const innerH = 297 - PAGE.padTop - PAGE.padBottom; // mm of usable height
    const w = window.open('', '', 'width=900,height=700');
    w.document.write(`<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8">
      <title>تعهد - ${registration.RegistrationNumber || ''}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;}
        html,body{font-family:Arial,'Segoe UI',sans-serif;direction:rtl;color:#000;}
        @page{size:A4 portrait;margin:0;}
        .sheet{position:relative;width:210mm;height:297mm;overflow:hidden;
               padding:${PAGE.padTop}mm ${PAGE.padSide}mm ${PAGE.padBottom}mm ${PAGE.padSide}mm;}
        /* Anchored top-right (RTL) so scaling shrinks toward the corner and stays
           inside the padding box. */
        .content{transform-origin:top right;font-size:17px;line-height:1.5;}
      </style>
      </head><body>
      <div class="sheet"><div class="content" id="pledge-content">${html}</div></div>
      <script>
        (function(){
          var content=document.getElementById('pledge-content');
          function fitAndPrint(){
            var probe=document.createElement('div');
            probe.style.cssText='position:absolute;visibility:hidden;height:100mm;';
            document.body.appendChild(probe);
            var pxPerMm=probe.getBoundingClientRect().height/100;
            document.body.removeChild(probe);
            var availPx=${innerH}*pxPerMm;
            var h=content.getBoundingClientRect().height;
            if(h>availPx){ content.style.transform='scale('+Math.max(0.4,(availPx/h)*0.99)+')'; }
            setTimeout(function(){ window.focus(); window.print(); }, 60);
          }
          if(document.readyState==='complete') fitAndPrint();
          else window.addEventListener('load', fitAndPrint);
        })();
      <\/script>
      </body></html>`);
    w.document.close();
  };

  // Print & Save: snapshot the current plan, persist wording+snapshot, then print.
  // Replacing an existing pledge overwrites the saved wording/snapshot and (if a
  // signed copy is on file) discards it — the old paper no longer matches — so
  // confirm first. Reprint is the safe, non-destructive option.
  const handlePrintAndSave = async () => {
    if (saved) {
      const warn = saved.HasScan
        ? 'This prints a NEW pledge and replaces the saved wording and plan snapshot. '
          + 'The signed copy currently on file will be REMOVED — you will need to re-scan the newly signed pledge.\n\n'
          + 'To reprint the existing signed document unchanged, use "Reprint" instead.\n\nContinue?'
        : 'This prints a NEW pledge and replaces the saved wording and plan snapshot.\n\n'
          + 'To reprint the existing one unchanged, use "Reprint" instead.\n\nContinue?';
      if (!window.confirm(warn)) return;
    }
    const rows = buildRowsFromPlan();
    const discounts = buildDiscountsFromProp();
    try {
      setBusy(true);
      await apiCall(`/registration/${registrationId}/pledge`, {
        method: 'POST',
        body: JSON.stringify({
          signerText: form.signer, pledgeText: form.pledge,
          closingText: form.closing, signDateText: form.signDate,
          planSnapshot: rows, discountsSnapshot: discounts
        })
      });
      showSuccess('Pledge saved');
      sendToPrinter(buildPledgeHTML(rows, discounts));
      await loadSaved();
    } catch (err) {
      showError('Failed to save pledge: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  // Reprint the saved document exactly, using its stored snapshot
  const handleReprint = () => {
    let rows = [];
    let discounts = [];
    try { rows = JSON.parse(saved.PlanSnapshot || '[]'); } catch (_) { rows = []; }
    try { discounts = JSON.parse(saved.DiscountsSnapshot || '[]'); } catch (_) { discounts = []; }
    sendToPrinter(buildPledgeHTML(rows, discounts));
  };

  const handleUploadScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // let the same file be re-picked later
    try {
      setBusy(true);
      const { fileName, fileData } = await compressScan(file);
      await apiCall(`/registration/${registrationId}/pledge/scan`, {
        method: 'POST',
        body: JSON.stringify({ fileName, fileData })
      });
      showSuccess(`Signed copy uploaded (${dataUrlKB(fileData)} KB)`);
      await loadSaved();
    } catch (err) {
      showError('Failed to upload scan: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  const openScan = async () => {
    // Fetch through apiCall's auth, then open a blob URL (avoids exposing token in URL)
    try {
      const res = await apiCall(`/registration/${registrationId}/pledge/scan`, { raw: true });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err) {
      showError('Failed to open scan: ' + err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold">Payment Pledge — تعهد</h3>
            {saved && (
              <p className="text-xs text-gray-500">
                Printed &amp; saved{saved.PrintedByName ? ` by ${saved.PrintedByName}` : ''} · {new Date(saved.PrintedAt).toLocaleString()}
                {saved.HasScan ? ' · signed copy on file' : ' · no signed copy yet'}
              </p>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {saved && (
              <button
                onClick={handleReprint}
                className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 flex items-center gap-2"
                title="Reprint the saved document exactly"
              >
                <RotateCcw size={15} /> Reprint
              </button>
            )}
            {allowEdit && (
              <button
                onClick={handlePrintAndSave}
                disabled={busy}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Printer size={15} /> {saved ? 'Print New & Save' : 'Print & Save'}
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <p className="text-center text-gray-500 py-6">Loading…</p>
          ) : (
            <>
              {/* Signed-copy row */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border">
                <div className="text-sm">
                  {saved?.HasScan ? (
                    <div className="text-green-700">
                      <span className="flex items-center gap-2">
                        <FileCheck size={16} /> Signed copy on file: {saved.ScannedFileName}
                      </span>
                      {(saved.ScannedByName || saved.ScannedAt) && (
                        <span className="block text-xs text-green-600 mt-0.5 ml-6">
                          Uploaded{saved.ScannedByName ? ` by ${saved.ScannedByName}` : ''}
                          {saved.ScannedAt ? ` · ${new Date(saved.ScannedAt).toLocaleString()}` : ''}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500">No signed copy attached yet.</span>
                  )}
                </div>
                <div className="flex gap-2">
                  {saved?.HasScan && (
                    <button onClick={openScan} className="px-3 py-1.5 text-blue-600 text-sm hover:underline">View</button>
                  )}
                  {allowEdit && (
                    <label className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 cursor-pointer ${
                      saved ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}>
                      <Upload size={14} /> {saved?.HasScan ? 'Replace scan' : 'Upload scan'}
                      <input type="file" accept="image/*,application/pdf" className="hidden"
                        disabled={!saved || busy} onChange={handleUploadScan} />
                    </label>
                  )}
                </div>
              </div>
              {!saved && allowEdit && (
                <p className="text-xs text-amber-600">Print &amp; Save first, then you can attach the signed copy.</p>
              )}
              {!saved && !allowEdit && (
                <p className="text-xs text-amber-600">No saved pledge to reprint yet — someone with print permission needs to create it first.</p>
              )}

              {/* Editable wording (edit mode only) */}
              {allowEdit && (
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Signer line (father — or "السيدة ... زوجة السيد ...")</label>
                  <textarea dir="rtl" rows={2} value={form.signer}
                    onChange={e => setForm(f => ({ ...f, signer: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Pledge sentence (year + totals prefilled)</label>
                  <textarea dir="rtl" rows={2} value={form.pledge}
                    onChange={e => setForm(f => ({ ...f, pledge: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Closing comment (standard, or a special arrangement — grant, etc.)</label>
                  <textarea dir="rtl" rows={2} value={form.closing}
                    onChange={e => setForm(f => ({ ...f, closing: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="max-w-xs">
                  <label className="block text-xs text-gray-500 mb-1">Signature date (Arabic)</label>
                  <input dir="rtl" type="text" value={form.signDate}
                    onChange={e => setForm(f => ({ ...f, signDate: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              )}

              {/* Preview — the current plan in edit mode; the exact saved pledge
                  in reprint-only mode (so it matches what Reprint will print). */}
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  {!allowEdit && saved ? 'Preview (saved pledge)' : 'Preview (current plan)'}
                </p>
                <div dir="rtl"
                  className="border-2 border-gray-300 rounded-lg p-6 bg-white shadow-inner text-base leading-relaxed"
                  style={{ fontFamily: "Arial, 'Segoe UI', sans-serif" }}
                  dangerouslySetInnerHTML={{ __html: (() => {
                    if (!allowEdit && saved) {
                      let r = [], d = [];
                      try { r = JSON.parse(saved.PlanSnapshot || '[]'); } catch (_) { r = []; }
                      try { d = JSON.parse(saved.DiscountsSnapshot || '[]'); } catch (_) { d = []; }
                      return buildPledgeHTML(r, d);
                    }
                    return buildPledgeHTML(buildRowsFromPlan(), buildDiscountsFromProp());
                  })() }} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationPledgePrintModal;
